import {
  Component,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { AsyncPipe } from '@angular/common';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
// Ag-Grid
import {
  GridOptions,
  GridApi,
  ColDef,
  CellContextMenuEvent,
  RowSelectionOptions,
} from 'ag-grid-community';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
// Services
import { SharedService } from '../../shared/services/shared.service';
import { ContextMenuService } from '../../shared/services/context-menu.service';
// Third Party
import { Subscription } from 'rxjs';

// components
import { EditBtnComponent } from 'src/app/shared/components/ag-grid/edit-btn/edit-btn.component';
import { SalesTableColumnDef } from './columnDefs';
import { SalesReceipt } from 'src/app/shared/models/accounting';
import { AccountingService } from '../services/accounting.service';
import {
  ComponentNames,
  ModalID,
  StatusModalIndicator,
} from 'src/app/shared/models/constants';
import { AgGridService } from 'src/app/shared/services/ag-grid.service';
import { isObjectEmpty } from 'src/app/shared/functions';

@Component({
  selector: 'app-sales-table',
  templateUrl: './sales-table.component.html',
  styleUrls: ['./sales-table.component.scss'],
  standalone: true,
  imports: [AgGridAngular, AsyncPipe, BsDropdownModule],
})
export class SalesTableComponent {
  @ViewChild('grid', { read: ElementRef }) gridElement!: ElementRef;
  @ViewChild('grid') grid!: AgGridAngular; // Keep for grid API access
  subscriptions = new Subscription();
  sales_records: SalesReceipt[] = [];

  public rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    enableSelectionWithoutKeys: true,
    checkboxes: false,
  };
  // ag-grid properties Grid
  gridApi!: GridApi;

  columnDefs: ColDef[] = SalesTableColumnDef;
  defaultColDef;
  gridOptions: GridOptions = {
    theme: agGridDarkTheme,
    suppressDragLeaveHidesColumns: true,
  };
  components: any;
  theme = agGridDarkTheme;

  // Context Menu state from service
  menuState$ = this.contextMenuService.menuState$;

  constructor(
    public sharedService: SharedService,
    public accountingService: AccountingService,
    private contextMenuService: ContextMenuService,
    private agGridService: AgGridService,
    private cdr: ChangeDetectorRef,
  ) {
    this.gridOptions = <GridOptions>{};
    this.gridOptions.headerHeight = 50;
    this.gridOptions.rowHeight = 28;
    this.gridOptions.enableCellTextSelection = true; // Allows text selection for copying
    this.gridOptions.ensureDomOrder = true; // Ensures proper DOM order for accessibility
    this.gridOptions.suppressClipboardPaste = false; // Allow clipboard operations
    this.gridOptions.context = { ConfigComponent: this };
    this.gridOptions.rowClass = 'ag-grid-row';
    this.defaultColDef = {
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
      wrapHeaderText: true,
    };
    this.components = { ViewEditBtns: EditBtnComponent };

    // sales_records_list_retrieved$
    this.subscriptions.add(
      this.accountingService.sales_records_list_retrieved$.subscribe(
        (sales_records) => {
          this.sales_records = JSON.parse(
            JSON.stringify(this.accountingService.sales_records),
          );
          this.cdr.detectChanges();
        },
      ),
    );
  }

  async onGridReady(params: any) {
    this.gridApi = params.api;

    if (!this.agGridService.hasCachedSettingsFor(ComponentNames.sales_table)) {
      await this.agGridService.loadSavedTableSettings(
        ComponentNames.sales_table,
      );
    }
    this.applyCurrentSessionPreferences();
  }
  async ngOnInit() {
    this.addButtonColumn();
    await this.getSalesRecords();
    this.getCustomerName();
  }
  ngAfterViewInit() {
    // Initialize service after gridElement is available
    this.contextMenuService.initialize(this.cdr, this.gridElement);
  }
  async getSalesRecords() {
    if (this.accountingService.sales_records.length > 0) {
      this.sales_records = JSON.parse(
        JSON.stringify(this.accountingService.sales_records),
      );
    } else {
      this.sharedService.openStatusSpinner('Loading Sales Records');
      await this.accountingService.getSalesReceipts();
      this.sharedService.closeStatusSpinner();
    }
  }
  async refreshTableDataClick() {
    this.sharedService.openStatusSpinner('Refreshing Sales Records');
    await this.accountingService.getSalesReceipts();
    this.sharedService.closeStatusSpinner();
  }
  getCustomerName() {
    // Uncomment and fix if needed
    /*
    this.sales_records.forEach((record) => {
      const customer_name = 
        this.accountingService.customers.find(
          (customer) => customer.id === record.customer_id
        ).contact.name_first +
        ' ' +
        this.accountingService.customers.find(
          (customer) => customer.id === record.customer_id
        ).contact.name_last;
      record.customer_name = customer_name;
    });
    this.sales_records = [...this.sales_records];
    */
  }
  addButtonColumn() {
    const hasEditDelete = this.columnDefs.some(
      (column) => column.headerName === 'View / Edit',
    );
    if (!hasEditDelete) {
      this.columnDefs.push({
        headerName: 'View / Edit',
        resizable: true,
        width: 100,
        pinned: 'right',
        cellRenderer: 'ViewEditBtns',
        cellRendererParams: {
          editItemClick: this.editReceiptClick.bind(this),
        },
      });
    }
  }
  editReceiptClick(selected_row: {
    colValues: SalesReceipt;
    rowIndex: number;
  }) {
    const expense = selected_row.colValues;
    this.sharedService.changeRoute('accounting-sales-receipt');
    setTimeout(() => {
      this.sharedService.view_edit_item$.next(expense);
    }, 125);
  }
  createNewSalesReceipt() {
    this.sharedService.changeRoute('accounting-sales-receipt');
  }
  createNewInvoice() {
    this.sharedService.changeRoute('accounting-sales-receipt');
    setTimeout(() => {
      this.accountingService.create_invoice$.next(true);
    }, 125);
  }
  closeClick() {
    this.sharedService.changeRoute('home');
  }

  // preference functions
  storeCurrentSessionFilters() {
    this.agGridService.storeCurrentSessionFilters(
      ComponentNames.sales_table,
      this.gridApi.getFilterModel(),
    );
  }
  storeCurrentSessionColumnConfig() {
    this.agGridService.storeCurrentSessionColumnConfig(
      ComponentNames.sales_table,
      this.gridApi?.getColumnState(),
    );
  }
  applyCurrentSessionPreferences() {
    let component: any = this.agGridService.current_session_filtering.find(
      (component) => component.component_name === ComponentNames.sales_table,
    );
    if (component && !isObjectEmpty(component.filters)) {
      this.gridApi.setFilterModel(component.filters);
    }
    component = this.agGridService.current_session_column_config.find(
      (component) => component.component_name === ComponentNames.sales_table,
    );
    if (component && !isObjectEmpty(component.column_config)) {
      this.gridApi?.applyColumnState({
        state: component.column_config,
        applyOrder: true,
      });
    }
  }
  saveCurrentTablePreferencesClick() {
    const filters = this.gridApi.getFilterModel();
    const column_state = this.gridApi?.getColumnState();
    this.agGridService.saveCurrentTableSettings(
      ComponentNames.sales_table,
      filters,
      column_state,
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // right click copy functions
  onCellContextMenu(event: CellContextMenuEvent) {
    this.contextMenuService.onCellContextMenu(event);
  }
  onMenuMouseEnter() {
    this.contextMenuService.onMenuMouseEnter();
  }
  onMenuMouseLeave() {
    this.contextMenuService.onMenuMouseLeave();
  }
  copyCellValue() {
    this.contextMenuService.copyCellValue();
  }
}
